import { AgentChatThreadId, CellId } from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { compareAsc, parseISO } from "date-fns";
import { keyBy, memoize, uniq } from "lodash";
import { createSelector } from "../utils/index.js";

import { AgentCellEditFragment } from "../../components/agent/shared/sharedThreadManagementUtils.generated";
import { RootState } from "../store";
import { createShallowResultSelector } from "../utils/index.js";

export type EditToReview = Omit<AgentCellEditFragment, "status"> & {
  status: "NEEDS_REVIEW";
};

export type PrefilledPrompt =
  | {
      type: "suggestion";
      text: string;
    }
  | {
      type: "error";
      cellId: CellId;
      errorTraceback: string;
    };

type AgentLogicViewState = {
  /**
   * This state holds any agent cell edits that need review from the user.
   */
  agentCellEditsToReview: EditToReview[];
  /**
   * A prompt that should prefill the prompt input when the chat opens.
   */
  prefilledPrompt: PrefilledPrompt | null;
};

/**
 * Maintains any global state which is referenced outside of the agent chat sidebar
 * in the logic view of the notebook.
 */
export const agentLogicViewSlice = createSlice({
  name: "agentLogicView",
  initialState: {
    agentCellEditsToReview: [],
    prefilledPrompt: null,
  } as AgentLogicViewState,
  reducers: {
    setAgentCellEdits: (
      state,
      action: PayloadAction<readonly AgentCellEditFragment[]>,
    ) => {
      state.agentCellEditsToReview = makeEditsToReviewState([
        ...action.payload,
      ]);
    },
    addOrUpdateAgentCellEdits: (
      state,
      action: PayloadAction<readonly AgentCellEditFragment[]>,
    ) => {
      const edits = {
        ...keyBy(state.agentCellEditsToReview, "id"),
        // Allow for newer updates to override any older edits.
        ...keyBy(action.payload, "id"),
      };
      state.agentCellEditsToReview = makeEditsToReviewState(
        Object.values(edits),
      );
    },
    setPrefilledPrompt: (
      state,
      action: PayloadAction<PrefilledPrompt | null>,
    ) => {
      state.prefilledPrompt = action.payload;
    },
  },
});

const makeEditsToReviewState = (
  edits: AgentCellEditFragment[],
): EditToReview[] =>
  edits.filter((e) => e.status === "NEEDS_REVIEW") as EditToReview[];

export const agentLogicViewSelectors = {
  /**
   * Selects the edits that need to be reviewed for a thread.
   */
  selectAllByThread: memoize((threadId: AgentChatThreadId) =>
    createSelector(
      [
        ({ agentLogicView }: RootState) =>
          agentLogicView.agentCellEditsToReview,
      ],
      (edits) => {
        return edits
          .filter((e: EditToReview) => e.agentChatThreadId === threadId)
          .sort((a, b) =>
            compareAsc(parseISO(a.createdDate), parseISO(b.createdDate)),
          );
      },
    ),
  ),
  /**
   * Selects the most recent agent cell edit for a cell.
   */
  createSelectLatestByCellId: (cellId: CellId) =>
    createShallowResultSelector(
      [
        ({ agentLogicView }: RootState) =>
          agentLogicView.agentCellEditsToReview,
      ],
      (edits) => {
        const cellEdits = edits.filter((e) => e.cellId === cellId);
        return cellEdits.sort((a, b) =>
          compareAsc(parseISO(a.createdDate), parseISO(b.createdDate)),
        )[0];
      },
    ),

  /**
   * Selects the most recent agent cell edit for a cell, and returns the index of the edit in the list and total number of edits.
   */
  createSelectLatestByCellIdWithPosition: (cellId: CellId) =>
    createShallowResultSelector(
      [
        ({ agentLogicView }: RootState) =>
          agentLogicView.agentCellEditsToReview,
      ],
      (edits) => {
        // Don't allow paginating to the deleted cell edits since
        // they don't show up in the UI.
        const visibleEdits = edits.filter(
          (e) => e.undoPayload?.type !== "DELETE_CELL",
        );
        const sortedEdits = visibleEdits.toSorted((a, b) =>
          compareAsc(parseISO(a.createdDate), parseISO(b.createdDate)),
        );
        const cellIdsWithEdits = uniq(sortedEdits.map((e) => e.cellId));
        const cellEditIndex = cellIdsWithEdits.findIndex((e) => e === cellId);
        return {
          edit: sortedEdits[cellEditIndex],
          index: cellEditIndex,
          total: cellIdsWithEdits.length,
        };
      },
    ),
  selectPrefilledPrompt: createSelector(
    [({ agentLogicView }: RootState) => agentLogicView.prefilledPrompt],
    (prefilledPrompt) => prefilledPrompt,
  ),
};

/**
 * Extracts the related cell ids from a list of agent cell edits.
 */
export const selectCellIdsFromEdits = createSelector(
  [(edits: readonly EditToReview[]) => edits],
  (edits) => uniq(edits.map((e) => e.cellId).filter((id) => id != null)),
);

/**
 * Extracts the related cell ids from a list of agent cell edits, excluding cells
 * that aren't visible in the UI (e.g., deleted cells).
 */
export const selectCellIdsFromVisibleEdits = createSelector(
  [(edits: readonly EditToReview[]) => edits],
  (edits) =>
    uniq(
      edits
        .filter((e) => e.undoPayload?.type !== "DELETE_CELL")
        .map((e) => e.cellId)
        .filter((id) => id != null),
    ),
);

export const agentLogicViewReducer = agentLogicViewSlice.reducer;

export const {
  addOrUpdateAgentCellEdits,
  setAgentCellEdits,
  setPrefilledPrompt,
} = agentLogicViewSlice.actions;
